import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ─────────────────────────────────────────────────────────────
// SOURCE UNIQUE DES CLIENTS — moteur de synchronisation
// Ce hook agrège les clients d'un marchand à partir de plusieurs
// sources (clientshop, commande, transaction) et les expose sous une
// forme homogène. Toutes les apps (Clients, Gestion Clients, Finance)
// consomment ce même hook, ce qui garantit des chiffres identiques
// partout et évite toute divergence entre les applications.
// ─────────────────────────────────────────────────────────────

export interface MerchantClient {
  clientId: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  totalOrders: number;
  totalTransactions: number;
  totalSpent: number;
  lastActivity: string;
  registered: boolean;
}

/** Convertit une valeur (varchar/numeric) en nombre de façon sûre. */
export function toAmount(v: unknown): number {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function useMerchantClients() {
  const { user } = useAuth();
  const merchantId = getCommerceId(user);

  const [clients, setClients] = useState<MerchantClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!merchantId) {
      setClients([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [shopRes, orderRes, txRes] = await Promise.all([
        supabase.from('clientshop').select('*').eq('idshop', merchantId).order('date', { ascending: false }),
        supabase.from('commande').select('*').eq('idvendeur', merchantId).order('date_time', { ascending: false }),
        supabase.from('transaction').select('*').eq('idcommerce', merchantId).order('date_time', { ascending: false }),
      ]);

      if (shopRes.error) throw shopRes.error;
      if (orderRes.error) throw orderRes.error;
      if (txRes.error) throw txRes.error;

      const map = new Map<string, MerchantClient>();
      const ensure = (id: string): MerchantClient => {
        if (!map.has(id)) {
          map.set(id, {
            clientId: id,
            name: '',
            email: '',
            phone: '',
            address: '',
            totalOrders: 0,
            totalTransactions: 0,
            totalSpent: 0,
            lastActivity: '',
            registered: false,
          });
        }
        return map.get(id) as MerchantClient;
      };

      // 1. Clients enregistrés du marchand
      (shopRes.data || []).forEach((c: Record<string, unknown>) => {
        const id = c.idclient != null ? String(c.idclient) : '';
        if (!id) return;
        const e = ensure(id);
        e.registered = true;
        e.name = (c.nomclient as string) || e.name;
        e.email = (c.email as string) || e.email;
        e.phone = (c.telephone as string) || e.phone;
        e.address = (c.adresse as string) || e.address;
        if (c.date && (c.date as string) > e.lastActivity) e.lastActivity = c.date as string;
      });

      // 2. Commandes du marchand
      (orderRes.data || []).forEach((o: Record<string, unknown>) => {
        // Le client peut être identifié par un compte Zifek (user_id) ou, pour
        // une commande invitée, par son email/nom pour ne pas perdre la trace.
        const rawUser = o.user_id != null ? String(o.user_id).trim() : '';
        const email = (o.email as string) || '';
        const id = rawUser || (email ? `guest:${email.toLowerCase()}` : '');
        if (!id) return;
        const e = ensure(id);
        e.totalOrders += 1;
        const paid = toAmount(o.sommepayee) || toAmount(o.totalttc);
        e.totalSpent += paid;
        e.name = (o.nomclient as string) || e.name;
        e.email = email || e.email;
        e.phone = (o.tel as string) || e.phone;
        e.address = (o.adresse as string) || e.address;
        if (o.date_time && (o.date_time as string) > e.lastActivity) e.lastActivity = o.date_time as string;
      });

      // 3. Transactions (paiements) du marchand
      (txRes.data || []).forEach((t: Record<string, unknown>) => {
        const id = t.idclient != null ? String(t.idclient) : '';
        if (!id) return;
        const e = ensure(id);
        e.totalTransactions += 1;
        e.totalSpent += toAmount(t.montant);
        if (t.date_time && (t.date_time as string) > e.lastActivity) e.lastActivity = t.date_time as string;
      });

      const result = Array.from(map.values()).sort((a, b) => b.totalSpent - a.totalSpent);
      setClients(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const stats = useMemo(() => {
    const totalSpent = clients.reduce((s, c) => s + c.totalSpent, 0);
    const withOrders = clients.filter((c) => c.totalOrders > 0).length;
    return {
      count: clients.length,
      withOrders,
      totalSpent,
      average: clients.length > 0 ? totalSpent / clients.length : 0,
    };
  }, [clients]);

  return { clients, loading, error, reload, stats, merchantId };
}

export default useMerchantClients;